import { useEffect, useState } from 'react';

import { Alert, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { unwrap } from 'pages/testrail/helpers';
import { useApp } from 'common/AppContext';

/**
 * Two-stage modal: (1) generate a TestRail case draft from the current
 * Azure DevOps work item via the AI helper, (2) review/edit and submit
 * to TestRail. Mirrors the SaveToTestRailModal shape for consistency
 * with the run-results "Save as TestRail case…" path.
 */
export const CreateTestRailFromWorkItemModal = ({ onHide, show, workItem }) => {

    const { api } = useApp();

    // 'generate' | 'review'
    const [phase, setPhase] = useState('generate');
    const [generating, setGenerating] = useState(false);
    const [draft, setDraft] = useState(null);

    const [title, setTitle] = useState('');
    const [preconds, setPreconds] = useState('');
    const [steps, setSteps] = useState('');

    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [sections, setSections] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [suiteId, setSuiteId] = useState('');
    const [sectionId, setSectionId] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {

        if (!show) return;
        setPhase('generate');
        setGenerating(false);
        setDraft(null);
        setTitle('');
        setPreconds('');
        setSteps('');
        setProjectId('');
        setSuiteId('');
        setSectionId('');
        setSuites([]);
        setSections([]);
        setError(null);
        setSuccess(null);

    }, [show, workItem]);

    const generate = async () => {

        if (!workItem || !workItem.id) return;

        setGenerating(true);
        setError(null);

        try {

            const data = await api.post(`/api/azure-devops/work-items/${workItem.id}/test-case`);
            setDraft(data);
            setTitle(data.title || '');
            setPreconds(data.custom_preconds || '');
            setSteps(data.custom_steps || '');
            setPhase('review');

            // Kick off project list early so it's ready when the user picks.
            api.get('/api/testrail/projects')
                .then(d => setProjects(unwrap(d, 'projects')))
                .catch(e => setError(e?.err || e?.message));

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setGenerating(false);

        }

    };

    useEffect(() => {

        setSuiteId('');
        setSectionId('');
        setSections([]);
        if (!projectId) {

            setSuites([]);
            return;

        }
        api.get(`/api/testrail/projects/${projectId}/suites`)
            .then(d => setSuites(unwrap(d, 'suites')))
            .catch(e => setError(e?.err || e?.message));

    }, [api, projectId]);

    useEffect(() => {

        setSectionId('');
        if (!projectId || !suiteId) {

            setSections([]);
            return;

        }
        api.get(`/api/testrail/projects/${projectId}/sections?suite_id=${suiteId}`)
            .then(d => setSections(unwrap(d, 'sections')))
            .catch(e => setError(e?.err || e?.message));

    }, [api, projectId, suiteId]);

    const submit = async () => {

        if (!sectionId || !title.trim()) return;

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        const payload = {
            refs: workItem ? `ADO #${workItem.id}` : undefined,
            title
        };
        if (preconds && preconds.trim()) payload.custom_preconds = preconds;
        if (steps && steps.trim()) payload.custom_steps = steps;

        try {

            const data = await api.post(`/api/testrail/sections/${sectionId}/cases`, payload);
            setSuccess(`Created TestRail case #${data?.id}.`);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setSubmitting(false);

        }

    };

    const canSubmit = !submitting && !!sectionId && !!title.trim();

    return (
        <Modal centered scrollable onHide={onHide} show={show} size="lg">

            <Modal.Header closeButton>
                <Modal.Title>
                    Create TestRail case from work item
                    {workItem && (
                        <span className="text-muted small ms-2">
                            ADO #
                            {workItem.id}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error && (
                    <Alert dismissible onClose={() => setError(null)} variant="danger">
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert dismissible onClose={() => setSuccess(null)} variant="success">
                        {success}
                    </Alert>
                )}

                {phase === 'generate' && (
                    <>
                        <p className="text-muted small mb-3">
                            The AI will read this work item — title, description, acceptance criteria, repro steps, comments, and any embedded screenshots — and draft a TestRail case (title, preconditions, steps) you can review before saving.
                        </p>
                        <Button disabled={generating} onClick={generate} variant="primary">
                            {generating ? (
                                <>
                                    <Spinner animation="border" as="span" className="me-2" size="sm" />
                                    Reading work item…
                                </>
                            ) : 'Generate test case draft'}
                        </Button>
                    </>
                )}

                {phase === 'review' && draft && (
                    <>
                        {draft.notes && (
                            <Alert className="small" variant="info">
                                <strong>
                                    Notes from the generator:
                                </strong>
                                {' '}
                                {draft.notes}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Title
                            </Form.Label>
                            <Form.Control onChange={e => setTitle(e.target.value)} value={title} />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Preconditions
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                onChange={e => setPreconds(e.target.value)}
                                rows={3}
                                value={preconds}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Steps
                                <small className="text-muted ms-2">
                                    (one per line — edit freely)
                                </small>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                onChange={e => setSteps(e.target.value)}
                                rows={10}
                                style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                value={steps}
                            />
                        </Form.Group>

                        <hr />

                        <div className="text-uppercase text-muted small mb-2" style={{ letterSpacing: 0.5 }}>
                            Save to
                        </div>

                        <Row className="g-2">
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Project
                                    </Form.Label>
                                    <Form.Select required onChange={e => setProjectId(e.target.value)} value={projectId}>
                                        <option value="">
                                            Select…
                                        </option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Suite
                                    </Form.Label>
                                    <Form.Select required disabled={!suites.length} onChange={e => setSuiteId(e.target.value)} value={suiteId}>
                                        <option value="">
                                            Select…
                                        </option>
                                        {suites.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Section
                                    </Form.Label>
                                    <Form.Select required disabled={!sections.length} onChange={e => setSectionId(e.target.value)} value={sectionId}>
                                        <option value="">
                                            Select…
                                        </option>
                                        {sections.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}

            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} variant="outline-secondary">
                    Close
                </Button>
                {phase === 'review' && (
                    <>
                        <Button onClick={generate} size="sm" variant="outline-secondary">
                            Regenerate
                        </Button>
                        <Button disabled={!canSubmit} onClick={submit} variant="primary">
                            {submitting ? (
                                <>
                                    <Spinner animation="border" as="span" className="me-2" size="sm" />
                                    Saving…
                                </>
                            ) : 'Save case'}
                        </Button>
                    </>
                )}
            </Modal.Footer>

        </Modal>
    );

};
